import { RootState } from '../store';
import { apiSlice } from './apiSlice';
import { TransportationPeriod } from '../../@types/configuration';
import { createEntityAdapter, createSelector, EntityState } from '@reduxjs/toolkit';
import { serializeLocalDateFields } from '../../utils/localDate';

const getValidFromTimestamp = (transportationPeriod: TransportationPeriod) => {
  if (!transportationPeriod.validFrom) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = new Date(transportationPeriod.validFrom).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const transportationPeriodAdapter = createEntityAdapter<TransportationPeriod>({
  sortComparer: (first, second) => {
    const firstTimestamp = getValidFromTimestamp(first);
    const secondTimestamp = getValidFromTimestamp(second);

    if (firstTimestamp === secondTimestamp) {
      return String(second.id).localeCompare(String(first.id), undefined, { numeric: true });
    }

    return secondTimestamp - firstTimestamp;
  },
});

const initialState = transportationPeriodAdapter.getInitialState();

export const transportationPeriodApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTransportationPeriod: builder.query<EntityState<TransportationPeriod, string>, void>({
      query: () => '/config/transportation-period',
      transformResponse: (response: TransportationPeriod[]) =>
        transportationPeriodAdapter.setAll(initialState, response),
      providesTags: ['TransportationPeriod'],
      keepUnusedDataFor: 300,
    }),
    createTransportationPeriod: builder.mutation({
      query: ({ transportationPeriod }) => ({
        url: '/config/transportation-period',
        method: 'POST',
        body: serializeLocalDateFields(transportationPeriod, ['validFrom', 'validUntil']),
      }),
      invalidatesTags: ['TransportationPeriod'],
    }),
    updateTransportationPeriod: builder.mutation({
      query: ({ transportationPeriod }) => ({
        url: '/config/transportation-period',
        method: 'PUT',
        body: serializeLocalDateFields(transportationPeriod, ['validFrom', 'validUntil']),
      }),
      invalidatesTags: ['TransportationPeriod'],
    }),
    deleteTransportationPeriod: builder.mutation({
      query: ({ id }) => ({
        url: `/config/transportation-period/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TransportationPeriod'],
    }),
  }),
});

export const {
  useGetTransportationPeriodQuery,
  useCreateTransportationPeriodMutation,
  useUpdateTransportationPeriodMutation,
  useDeleteTransportationPeriodMutation,
} = transportationPeriodApiSlice;

export const selectTransportationPeriodResults =
  transportationPeriodApiSlice.endpoints.getTransportationPeriod.select();

const selectTransportationPeriodData = createSelector(
  selectTransportationPeriodResults,
  (transportationPeriodResult) => transportationPeriodResult.data
);
export const {
  selectAll: selectAllTransportationPeriods,
  selectById: selectTransportationPeriodById,
} = transportationPeriodAdapter.getSelectors<RootState>(
  (state) => selectTransportationPeriodData(state) ?? initialState
);
